import React, { useState, cloneElement } from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  makeStyles,
  IconButton,
  Theme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Link
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Domain, DomainItem } from 'models/Domain';
import Typography from 'components/Typography';
import { ContainerSizeProp } from '@material-ui/core/styles/makeStyles';
import { PaginationComponent } from 'components/Pagination';
import { ApiQueryParams } from 'store/utils';
import { CrudCurriedFn } from 'components/Crud';
import { capitalise } from 'utils';
import { getDomainSlice } from 'store';
import { useSelector } from 'react-redux';

interface HasResultsProp extends ContainerSizeProp {
  hasResults?: boolean;
}

type TextOverflow = {
  shouldTextOverflow?: boolean;
};

export const useTableStyles = makeStyles<
  Theme,
  Partial<HasResultsProp & TextOverflow>
>(
  theme => ({
    tableContainer: {
      display: 'flex',
      flexDirection: 'column',
      width: ({ containerSize = 'large' }) => theme.containers[containerSize],
      margin: '0 auto',
      backgroundColor: theme.palette.common.white,
      borderRadius: theme.spacing(0.5),
      boxShadow: '0px 2px 2px 0px rgba(0, 0, 0, 0.25)'
    },
    table: {
      backgroundColor: theme.palette.common.white,
      boxShadow: 'none',
      tableLayout: 'fixed',
      borderRadius: ({ hasResults }) => (!hasResults ? theme.spacing(0.5) : 0)
    },
    tableHeader: {
      padding: theme.spacing(2, 1),
      backgroundColor: theme.palette.aegis.colour.grey.darkest,
      color: theme.palette.common.white,
      borderBottom: 'none',
      borderRight: `1px dashed ${theme.palette.aegis.colour.grey.light}`,
      '&:last-child': {
        borderRight: 'none'
      }
    },
    tableHeaderCellContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    headerButton: {
      padding: 0,
      marginLeft: theme.spacing(1),
      color: theme.palette.aegis.semantic.message.light,
      fontSize: theme.spacing(3)
    },
    tableRow: {
      '& td:first-child': {
        textOverflow: 'unset'
      },
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.aegis.colour.grey.lightest
      },
      '&&:hover': {
        backgroundColor: theme.palette.primary.light
      }
    },
    tableCell: {
      height: theme.spacing(10),
      padding: theme.spacing(1, 2),
      color: theme.palette.common.black,
      borderBottom: 'none',
      borderRight: `1px dashed ${theme.palette.aegis.colour.grey.light}`,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: ({ shouldTextOverflow = true }) =>
        shouldTextOverflow ? 'ellipsis' : 'unset',
      '&:last-child': {
        borderRight: 'none'
      }
    },
    firstCell: {
      padding: 0,
      textAlign: 'center',
      verticalAlign: 'middle',
      textOverflow: 'inherit',
      '& [class*="MuiFormControlLabel-root"]': {
        margin: 0
      },
      '& [class*="MuiFormHelperText-root"]': {
        display: 'none'
      }
    },
    actionsButtonCell: {
      padding: 0,
      textAlign: 'center'
    },
    twoLineTruncate: {
      display: '-webkit-box',
      '-webkit-line-clamp': 2,
      '-webkit-box-orient': 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'normal'
    },
    noResultsRow: {
      height: 577
    },
    noResultsMessage: {
      '& > * + *': {
        marginTop: theme.spacing(1)
      },
      textAlign: 'center',
      textTransform: 'none'
    },
    noResultsAction: {
      textTransform: 'capitalize'
    },
    menuItem: {
      height: theme.spacing(6),
      width: theme.spacing(20),
      border: `1px solid ${theme.palette.aegis.semantic.message.dark}`,
      backgroundColor: theme.palette.aegis.colour.grey.darkest,
      color: theme.palette.aegis.semantic.border.light,
      '&:not(:first-of-type)': {
        marginTop: -1
      },
      '&:hover, &.Mui-focusVisible': {
        backgroundColor: theme.palette.aegis.colour.grey.darkest
      },
      '& .MuiListItemText-root': {
        flex: 'none'
      }
    },
    menuItemText: {
      fontSize: 14,
      fontWeight: 600
    },
    menuIcon: {
      color: theme.palette.aegis.semantic.background.canvas,
      fontSize: theme.spacing(3)
    },
    menuButton: {
      fontSize: theme.spacing(3)
    }
  }),
  { name: 'ListCapacityTable' }
);

export interface Column {
  label: string;
  renderButton?: (className: string) => React.ReactNode;
}

interface ListTableProps {
  columnsWidth: number[];
  children: JSX.Element[];
  label: string;
  columnHeaders: Column[];
  domain: Domain;
  queryParams?: ApiQueryParams;
  rowHovered?: number | boolean;
  setRowHovered?: (newRowHovered: boolean | number) => void;
  noResultsActionLabel?: string;
}

export const useTableRowActionsState = () => {
  const [rowHovered, setRowHovered] = useState<boolean | number>(false);
  return { rowHovered, setRowHovered };
};

export function ListCapacityTable(props: ListTableProps) {
  const {
    label,
    columnsWidth,
    columnHeaders,
    children,
    domain,
    queryParams,
    rowHovered,
    setRowHovered,
    noResultsActionLabel = `Add ${domain.label}`
  } = props;

  const hasResults = Array.isArray(children)
    ? children.length > 0
    : Boolean(children);

  const classes = useTableStyles({ hasResults });

  const handleMouseEnter = (index: number) => () => {
    if (setRowHovered && rowHovered !== index) {
      setRowHovered(index);
    }
  };

  const handleMouseLeave = () => {
    if (setRowHovered && rowHovered !== false) {
      setRowHovered(false);
    }
  };

  const { selector } = getDomainSlice(domain);
  const { error } = useSelector(selector);

  return (
    <div className={classes.tableWrapper}>
      <TableContainer className={classes.table} data-testid="listTable">
        <Table stickyHeader className={classes.table} aria-label={label}>
          <TableHead>
            <TableRow>
              {columnHeaders.map((column, i) => (
                <TableCell
                  width={columnsWidth[i]}
                  key={column.label}
                  className={classes.tableHeader}
                >
                  <div className={classes.tableHeaderCellContent}>
                    {column.label}
                    {column.renderButton?.(classes.headerButton)}
                  </div>
                </TableCell>
              ))}

              <TableCell
                key="capacityTypesListTableActionsColumn"
                width={columnsWidth[columnsWidth.length - 1]}
                className={classes.tableHeader}
              >
                &nbsp;
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody onMouseLeave={handleMouseLeave}>
            {(() => {
              if (hasResults) {
                return children.map((child: JSX.Element, index: number) =>
                  cloneElement(child, {
                    onMouseEnter: handleMouseEnter(index)
                  })
                );
              }
              if (!hasResults && error !== null) {
                return (
                  <TableRow
                    className={classes.noResultsRow}
                    data-testid="errorRow"
                  >
                    <TableCell
                      // Plus 2 because of the checkbox and menu cells
                      colSpan={columnHeaders.length + 2}
                      className={classes.noResultsMessage}
                    >
                      <Typography
                        variant="h3"
                        component="h2"
                        className={classes.noResultsHeading}
                      >
                        Something went wrong
                      </Typography>
                      <Typography
                        component="p"
                        className={classes.noResultsDescription}
                      >
                        The table couldn’t load properly
                      </Typography>
                      {/* This rule below is disabled because it doesn't realise that the link is being replaced with a button element */}
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <Link
                        component="button"
                        variant="body1"
                        onClick={() => {
                          window.location.reload();
                        }}
                        underline="always"
                        className={classes.noResultsAction}
                      >
                        Try again
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              }

              return (
                <TableRow
                  className={classes.noResultsRow}
                  data-testid="noResultsRow"
                >
                  <TableCell
                    // Plus 2 because of the checkbox and menu cells
                    colSpan={columnHeaders.length + 2}
                    className={classes.noResultsMessage}
                  >
                    <Typography
                      variant="h3"
                      component="h2"
                      className={classes.noResultsHeading}
                    >
                      Nothing here yet
                    </Typography>
                    <Typography
                      component="p"
                      className={classes.noResultsDescription}
                    >
                      Get started by adding your first {domain.label}
                    </Typography>
                    {/* This rule below is disabled because it doesn't realise that the link is being replaced with a button element */}
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <Link
                      component="button"
                      variant="body1"
                      onClick={() => null}
                      underline="always"
                      className={classes.noResultsAction}
                    >
                      {noResultsActionLabel}
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })()}
          </TableBody>
        </Table>
      </TableContainer>

      <PaginationComponent
        domain={domain}
        queryParams={queryParams}
        hideBorder
      />
    </div>
  );
}

interface TableActionsMenuItems {
  label: string;
  icon: JSX.Element;
  onClick: CrudCurriedFn;
  overrideUrl?: string;
}
interface TableActionsProps {
  menuItems: TableActionsMenuItems[];
  domainItem: DomainItem;
}

export function TableActions({ menuItems, domainItem }: TableActionsProps) {
  const classes = useTableStyles({ shouldTextOverflow: false });
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorElement(null);
  };

  return (
    <TableCell className={`${classes.tableCell} ${classes.actionsButtonCell}`}>
      <IconButton
        onClick={handleOpenMenu}
        data-testid="tableRowActionsMenu"
        className={classes.menuButton}
      >
        <MoreVertIcon fontSize="inherit" />
      </IconButton>
      <Menu
        anchorEl={anchorElement}
        open={Boolean(anchorElement)}
        onClose={handleCloseMenu}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'right'
        }}
        MenuListProps={{ disablePadding: true }}
      >
        {menuItems.map(({ label, icon, onClick, overrideUrl }) => (
          <MenuItem
            key={label}
            onClick={event => {
              onClick(domainItem, overrideUrl)(event);
              handleCloseMenu();
            }}
            data-testid={`tableRow${capitalise(label)}`}
            className={classes.menuItem}
          >
            <ListItemIcon>
              {cloneElement(icon, { fontSize: 'inherit', color: 'inherit' })}
            </ListItemIcon>
            <ListItemText
              primary={label}
              primaryTypographyProps={{ className: classes.menuItemText }}
            />
          </MenuItem>
        ))}
      </Menu>
    </TableCell>
  );
}
